import { PROPERTY_FILTER_KEYS } from '@/shared/utils/property-filter-keys';

type AdvancedFilters = Record<string, any>;
type QueryValue = string | number | boolean;

/**
 * Descriptor for one advanced-filter field: how to detect it as active,
 * how to serialize it to a query value, and how to render its chip label.
 */
interface FilterFieldDescriptor {
  key: string;
  isActive: (value: unknown) => boolean;
  toQuery: (value: any) => QueryValue;
  labelPrefix?: string;
  toLabel?: (value: any) => string;
  /**
   * Whether this field counts toward the "Filtros (N)" badge. Defaults to
   * true; set false only for a field with no panel control of its own
   * (like `import_id`) -- counted, it reads as "1 filter is active, go
   * open the panel to see which" and the panel has nothing to show,
   * which is the bug this flag exists to avoid.
   */
  countsTowardBadge?: boolean;
}

/**
 * Reports whether a scalar string value is present (truthy).
 *
 * @param value - The value to test.
 * @returns True when the value is truthy.
 */
const isTruthy = (value: unknown): boolean => Boolean(value);
/**
 * Reports whether an array value exists and has at least one item.
 *
 * @param value - The value to test.
 * @returns True for a non-empty array.
 */
const isNonEmptyArray = (value: unknown): boolean => Array.isArray(value) && value.length > 0;
/**
 * Reports whether a numeric value has been explicitly set (not undefined).
 *
 * @param value - The value to test.
 * @returns True when the value is defined.
 */
const isDefined = (value: unknown): boolean => value !== undefined;

/**
 * The API's own status vocabulary, in the words the rest of the app shows.
 *
 * The chip is the only place a reader meets this value, and the raw one
 * reads as a leak: the table beside it says "Ativo" for the same listing.
 * Every other label in this table is hardcoded Portuguese too.
 */
const STATUS_LABELS: Record<string, string> = {
  active: 'Ativo', // i18n-exempt: pt-BR-only chip label; this whole table awaits i18n
  inactive: 'Inativo', // i18n-exempt: pt-BR-only chip label; this whole table awaits i18n
  sold: 'Vendido', // i18n-exempt: pt-BR-only chip label; this whole table awaits i18n
  rented: 'Alugado', // i18n-exempt: pt-BR-only chip label; this whole table awaits i18n
  suspended: 'Suspenso', // i18n-exempt: pt-BR-only chip label; this whole table awaits i18n
};

/**
 * Central table describing every advanced filter field once, so that
 * counting, query building, and chip labeling all iterate the same source.
 */
const FILTER_FIELDS: FilterFieldDescriptor[] = [
  // No chip label on purpose: the post-import strip is what turns this on
  // and off, so a second control for it would be a second source of truth.
  // Not counted toward the badge either, for the same reason: the filter
  // panel has no field for it, so a badge claiming "1" with nothing to
  // show when opened was the bug, not a feature.
  { key: 'import_id', isActive: isTruthy, toQuery: (v) => v, countsTowardBadge: false },
  { key: 'imported', isActive: (v) => v === true, toQuery: () => 'true', labelPrefix: 'Origem:', toLabel: () => 'Importados via planilha' }, // i18n-exempt: pt-BR-only chip label; this whole table awaits i18n
  { key: 'created_after', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'Cadastrado desde', toLabel: (v) => `Cadastrado desde ${new Date(`${v}T00:00:00`).toLocaleDateString('pt-BR')}` }, // i18n-exempt: pt-BR-only chip label; this whole table awaits i18n
  { key: 'days_since_visit_gte', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Sem visita há', toLabel: (v) => `Sem visita há ${v}+ dias` }, // i18n-exempt: pt-BR-only chip label; this whole table awaits i18n
  { key: 'sort', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'Ordem:', toLabel: () => 'Mais tempo na carteira' },
  { key: 'publication_blocked', isActive: (v) => v === true, toQuery: () => 'true', labelPrefix: 'Publicação:', toLabel: () => 'Publicação bloqueada' },
  { key: 'favorites', isActive: (v) => v === true, toQuery: () => '1', labelPrefix: 'Favoritos', toLabel: () => 'Somente favoritos' }, // i18n-exempt: pt-BR-only chip label; this whole table awaits i18n
  { key: 'status', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'Situação:', toLabel: (v) => `Situação: ${STATUS_LABELS[v] ?? v}` },
  { key: 'state', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'UF:', toLabel: (v) => `UF: ${v}` },
  { key: 'city', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'Cidade:', toLabel: (v) => `Cidade: ${v}` },
  { key: 'neighborhood', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'Bairro:', toLabel: (v) => `Bairro: ${v}` },
  { key: 'category', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Tipo:', toLabel: (v) => `Tipo: ${v.join(', ')}` },
  { key: 'transaction_type', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Transação:', toLabel: (v) => `Transação: ${v.join(', ')}` },
  { key: 'sale_price_min', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Venda ≥', toLabel: (v) => `Venda ≥ ${v}` },
  { key: 'sale_price_max', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Venda ≤', toLabel: (v) => `Venda ≤ ${v}` },
  { key: 'rent_price_min', isActive: isDefined, toQuery: (v) => v },
  { key: 'rent_price_max', isActive: isDefined, toQuery: (v) => v },
  { key: 'bedrooms', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Quartos:', toLabel: (v) => `Quartos: ${v}+` },
  { key: 'bathrooms', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Banheiros:', toLabel: (v) => `Banheiros: ${v}+` },
  { key: 'parking_spots', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Vagas:', toLabel: (v) => `Vagas: ${v}+` },
  { key: 'suites', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Suítes:', toLabel: (v) => `Suítes: ${v}+` },
  { key: 'built_area_min', isActive: isDefined, toQuery: (v) => v },
  { key: 'built_area_max', isActive: isDefined, toQuery: (v) => v },
  { key: 'area_min', isActive: isDefined, toQuery: (v) => v },
  { key: 'area_max', isActive: isDefined, toQuery: (v) => v },
  { key: 'condo_fee_min', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Condom. ≥', toLabel: (v) => `Condom. ≥ ${v}` },
  { key: 'condo_fee_max', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Condom. ≤', toLabel: (v) => `Condom. ≤ ${v}` },
  { key: 'condo_payment_frequency', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'Freq.cond:', toLabel: (v) => `Freq.cond: ${v}` },
  { key: PROPERTY_FILTER_KEYS.acceptsPets, isActive: (v) => v === true, toQuery: () => 'true', labelPrefix: 'Pets:', toLabel: () => 'Aceita pets' },
  { key: 'furnished', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'Mobília:', toLabel: (v) => `Mobília: ${v}` },
  { key: 'has_virtual_tour', isActive: (v) => v === true, toQuery: () => 'true', labelPrefix: 'Tour:', toLabel: () => 'Tour virtual' },
  { key: 'facing', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'Orientação:', toLabel: (v) => `Orientação: ${v}` },
  { key: 'exchange', isActive: (v) => v === true, toQuery: () => 'true', labelPrefix: 'Permuta:', toLabel: () => 'Aceita permuta' },
  { key: 'year_built_min', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Ano ≥', toLabel: (v) => `Ano ≥ ${v}` },
  { key: 'year_built_max', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Ano ≤', toLabel: (v) => `Ano ≤ ${v}` },
  { key: 'floor_min', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Andar ≥', toLabel: (v) => `Andar ≥ ${v}` },
  { key: 'floor_max', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Andar ≤', toLabel: (v) => `Andar ≤ ${v}` },
  { key: 'total_rooms', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Cômodos:', toLabel: (v) => `Cômodos: ${v}+` },
  { key: 'amenities_internal', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Amen.int:', toLabel: (v) => `Amen.int: ${v.join(', ')}` },
  { key: 'amenities_external', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Amen.ext:', toLabel: (v) => `Amen.ext: ${v.join(', ')}` },
  { key: 'amenities_security', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Amen.seg:', toLabel: (v) => `Amen.seg: ${v.join(', ')}` },
  { key: 'condo_leisure', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Lazer:', toLabel: (v) => `Lazer: ${v.join(', ')}` },
  { key: 'condo_security', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Seg.cond:', toLabel: (v) => `Seg.cond: ${v.join(', ')}` },
  { key: 'condo_expenses', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Desp.cond:', toLabel: (v) => `Desp.cond: ${v.join(', ')}` },
  { key: 'rent_payment_frequency', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'Freq.alug:', toLabel: (v) => `Freq.alug: ${v}` },
  { key: 'rent_guarantee', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Garantia:', toLabel: (v) => `Garantia: ${v.join(', ')}` },
  { key: 'rent_included_expenses', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Desp.incl:', toLabel: (v) => `Desp.incl: ${v.join(', ')}` },
  { key: 'lease_term_months', isActive: isDefined, toQuery: (v) => v, labelPrefix: 'Prazo:', toLabel: (v) => `Prazo: ${v} meses` },
  { key: 'administration_type', isActive: isTruthy, toQuery: (v) => v, labelPrefix: 'Admin:', toLabel: (v) => `Admin: ${v}` },
  { key: 'parking_type', isActive: isNonEmptyArray, toQuery: (v) => v.join(','), labelPrefix: 'Tipo vaga:', toLabel: (v) => `Tipo vaga: ${v.join(', ')}` },
];

/**
 * Selects the filter-field descriptors whose corresponding filter value is
 * currently active.
 *
 * @param filters - The advanced filters map.
 * @returns The active field descriptors.
 */
const activeFields = (filters: AdvancedFilters): FilterFieldDescriptor[] =>
  FILTER_FIELDS.filter((field) => field.isActive(filters[field.key]));

/**
 * Counts how many advanced filters are currently active.
 * @returns The number of active filter fields.
 */
export function countActiveFilters(filters: AdvancedFilters): number {
  return activeFields(filters).filter((field) => field.countsTowardBadge !== false).length;
}

/**
 * Builds the backend query object from the active advanced filters.
 * @returns A map of query parameter names to serialized values.
 */
export function buildAdvancedFilterQuery(
  advancedFilters: AdvancedFilters
): Record<string, QueryValue> {
  return activeFields(advancedFilters).reduce<Record<string, QueryValue>>((query, field) => {
    query[field.key] = field.toQuery(advancedFilters[field.key]);
    return query;
  }, {});
}

/**
 * Builds the human-readable chip labels for active, labelable filters.
 * @returns An ordered list of chip label strings.
 */
export function buildFilterChipLabels(advancedFilters: AdvancedFilters): string[] {
  return activeFields(advancedFilters)
    .filter((field) => Boolean(field.toLabel))
    .map((field) => field.toLabel!(advancedFilters[field.key]));
}

/**
 * Removes the advanced filter matching a given chip label.
 *
 * The removed key is set to `undefined`, not deleted. The result goes
 * straight into `useUrlFilters`' `setFilters`, which MERGES its argument onto
 * the current filters -- and a merge cannot see an absent key, so `delete`
 * left the filter exactly where it was and the chip's × did nothing at all.
 * `undefined` reads as the field's default, which is what the hook strips
 * from the URL.
 *
 * @returns A new filters object with the removed field cleared.
 */
export function removeAdvancedFilterByLabel(
  advancedFilters: AdvancedFilters,
  label: string
): AdvancedFilters {
  const next = { ...advancedFilters };
  const target = FILTER_FIELDS.find((field) => field.labelPrefix && label.startsWith(field.labelPrefix));
  if (target) next[target.key] = undefined;
  return next;
}

export { mapFilterPanelToStore } from './manage-properties-panel-mappings';

/**
 * Builds the advanced-filter patch that narrows the list to one group, so a
 * group funnel reuses the SAME filter pipeline (chip, Filtros count, export)
 * as every other filter.
 *
 * The patch keys match the store keys the filter panel already writes
 * (`city`, `state`, `neighborhood`, `category[]`, `transaction_type[]`,
 * `status`), so the chip renders through `buildFilterChipLabels` and removes
 * like any other.
 *
 * @param by - The active grouping dimension.
 * @param label - The group's primary label (city/neighborhood literal, or the
 *   raw category/status value).
 * @param transactionTypes - For the `business` dimension, the backend
 *   transaction-type values the group represents (e.g. `['Venda']`).
 * @param subLabel - The group's secondary label: the state (UF) for a `city`
 *   group, the parent city for a `neighborhood` group. Carried through so two
 *   same-named cities in different states filter to the right one.
 * @returns The partial advanced-filter object to merge via `setFilters`.
 */
export function groupFilterPatch(
  by: 'city' | 'neighborhood' | 'category' | 'business' | 'status'
    | 'total_area' | 'constructed_area' | 'sale_price' | 'rental_price',
  label: string,
  transactionTypes: string[],
  subLabel?: string,
): AdvancedFilters {
  const build = GROUP_FILTER_PATCH_BUILDERS[by];
  return build ? build(label, transactionTypes, subLabel) : {};
}

/** Signature of a per-dimension group-filter patch builder. */
type GroupFilterPatchBuilder = (
  label: string,
  transactionTypes: string[],
  subLabel?: string,
) => AdvancedFilters;

/**
 * Per-dimension builders for {@link groupFilterPatch}. A dimension absent from
 * this table (the banded area/price groups, which have no single-value filter
 * control) produces no patch -- the same outcome the old `default` branch gave.
 */
const GROUP_FILTER_PATCH_BUILDERS: Partial<Record<string, GroupFilterPatchBuilder>> = {
  // The state disambiguates same-named cities; omit it only when absent so the
  // filter stays as specific as the group the user clicked.
  city: (label, _tx, subLabel) => (subLabel ? { city: label, state: subLabel } : { city: label }),
  neighborhood: (label) => ({ neighborhood: label }),
  category: (label) => ({ category: [label] }),
  business: (_label, transactionTypes) => ({ transaction_type: transactionTypes }),
  status: (label) => ({ status: label }),
};
